import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import TextEditor from 'components/TextEditor';
import { openPopupWindow, downloadFile } from 'helpers/generic';
import { getRouterBasename } from 'helpers/config';

const StyledCard = styled.div`
	position: relative;

	.notes-content {
		min-height: 100px;
		padding: 15px;
		font-size: 14px;
		line-height: 1.6;

		&.empty {
			color: #999;
			font-style: italic;
		}
	}

	.btn-icon {
		background: none;
		border: none;
		color: var(--bs-primary);
		font-size: 14px;
		cursor: pointer;
		padding: 0;
		transition: all 0.2s;

		&:hover {
			color: var(--bs-primary);
			opacity: 0.7;
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}

	.files-section {
		border-top: 1px solid var(--bs-gray-200);
		padding: 10px 15px;
		display: grid;
		grid-template-columns: 1fr 1fr 1fr;
		gap: 4px 8px;
	}

	.file-item {
		display: flex;
		align-items: center;
		gap: 5px;
		font-size: 13px;
		min-width: 0;
		overflow: hidden;

		i {
			color: var(--bs-primary);
			flex-shrink: 0;
		}

		a {
			color: var(--bs-body-color);
			text-decoration: none;
			cursor: pointer;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			display: block;

			&:hover {
				text-decoration: underline;
				color: var(--bs-primary);
			}
		}
	}

	.drop-overlay {
		position: absolute;
		inset: 0;
		background: rgba(var(--bs-primary-rgb), 0.1);
		border: 2px dashed var(--bs-primary);
		border-radius: 0.375rem;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 10;
		font-weight: 500;
		color: var(--bs-primary);
	}
`;

let axiosCancelToken = null;

export default function Notes({ community_id, name }) {
	const [isEditing, setIsEditing] = useState(false);
	const [notesContent, setNotesContent] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [files, setFiles] = useState([]);
	const [folderId, setFolderId] = useState(null);
	const [isUploading, setIsUploading] = useState(false);
	const [isDragging, setIsDragging] = useState(false);

	const fileInputRef = useRef(null);
	const dragCounter = useRef(0);

	// Generate dynamic key based on community_id
	const optionKey = community_id ? `dashboard_notes_${community_id}` : 'dashboard_notes';

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		return function cleanup() {
			axiosCancelToken.cancel();
		}
	}, []);

	useEffect(() => {
		loadNotes();
		loadFiles();
		loadFolderId();
	}, [community_id]);

	// BroadcastChannel listener
	useEffect(() => {
		const channel = new BroadcastChannel('documents-channel');
		channel.onmessage = (event) => {
			if (event.data.type === 'files-changed') {
				loadFiles();
			}
		};
		return () => channel.close();
	}, [community_id]);

	const loadNotes = async () => {
		setIsLoading(true);
		try {
			const config = {
				cancelToken: axiosCancelToken.token
			};

			// Add community_id as query param if present
			if (community_id) {
				config.params = { community_id };
			}

			const response = await axios.get(`/api/manager/options/get/${optionKey}`, config);
			setNotesContent(response.data || '');
		} catch (error) {
			if (!axios.isCancel(error)) {
				console.error('Error loading notes:', error);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const loadFiles = async () => {
		try {
			const params = {};
			if (community_id) params.community_id = community_id;

			const response = await axios.get('/api/manager/dashboard-notes/files', {
				params,
				cancelToken: axiosCancelToken.token
			});
			setFiles(response.data || []);
		} catch (error) {
			if (!axios.isCancel(error)) {
				console.error('Error loading files:', error);
			}
		}
	};

	const loadFolderId = async () => {
		try {
			const response = await axios.get('/api/manager/dashboard-notes/folder-id', {
				cancelToken: axiosCancelToken.token
			});
			setFolderId(response.data.folder_id);
		} catch (error) {
			if (!axios.isCancel(error)) {
				console.error('Error loading folder id:', error);
			}
		}
	};

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const data = {
				value: notesContent
			};

			// Add community_id to request body if present
			if (community_id) {
				data.community_id = community_id;
			}

			await axios.post(`/api/manager/options/save/${optionKey}`, data, {
				cancelToken: axiosCancelToken.token
			});
			setIsEditing(false);
		} catch (error) {
			if (!axios.isCancel(error)) {
				console.error('Error saving notes:', error);
				alert('Error al guardar las notas');
			}
		} finally {
			setIsSaving(false);
		}
	};

	const toggleEdit = () => {
		setIsEditing(!isEditing);
	};

	const getFileIcon = (file) => {
		if (file.extension === 'pdf') return 'bi bi-file-pdf';
		if (file.mimetype && file.mimetype.indexOf('image/') !== -1) return 'bi bi-file-earmark-image';
		if (file.extension && (file.extension.indexOf('xls') !== -1 || file.extension === 'ods')) return 'bi bi-file-earmark-excel';
		if (file.extension && (file.extension.indexOf('doc') !== -1 || file.extension === 'odt')) return 'bi bi-file-earmark-word';
		return 'bi bi-file-fill';
	};

	const handleFileClick = (e, file) => {
		e.preventDefault();
		if (file.viewable) {
			openPopupWindow(getRouterBasename() + '/documents/view-file/' + file.id);
		} else {
			downloadFile('/api/manager/documents/download-file/' + file.id);
		}
	};

	const openDocumentsFolder = () => {
		if (folderId) {
			openPopupWindow(getRouterBasename() + '/documents/?folder_id=' + folderId);
		}
	};

	const uploadFiles = async (fileList) => {
		if (!fileList.length || !folderId) return;

		setIsUploading(true);
		try {
			for (let i = 0; i < fileList.length; i++) {
				const formData = new FormData();
				formData.append('file', fileList[i]);
				formData.append('folder_id', folderId);
				if (community_id) formData.append('community_id', community_id);

				await axios.post('/api/manager/documents/upload-file', formData, {
					headers: { 'Content-Type': 'multipart/form-data' },
					cancelToken: axiosCancelToken.token
				});
			}
			loadFiles();
			// Broadcast so other widgets/tabs refresh
			const channel = new BroadcastChannel('documents-channel');
			channel.postMessage({ type: 'files-changed' });
			channel.close();
		} catch (error) {
			if (!axios.isCancel(error)) {
				console.error('Error uploading files:', error);
				alert('Error al subir archivos');
			}
		} finally {
			setIsUploading(false);
		}
	};

	const handleFileInputChange = (e) => {
		uploadFiles(e.target.files);
		e.target.value = null;
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current = 0;
		setIsDragging(false);

		if (e.dataTransfer.files.length) {
			uploadFiles(e.dataTransfer.files);
		}
	};

	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current++;
		if (e.dataTransfer.types.includes('Files')) {
			setIsDragging(true);
		}
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current--;
		if (dragCounter.current === 0) {
			setIsDragging(false);
		}
	};

	return (
		<StyledCard
			className="card border-0 shadow-sm mb-3"
			onDrop={handleDrop}
			onDragEnter={handleDragEnter}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
		>
			{isDragging && (
				<div className="drop-overlay">Suelta los archivos aquí</div>
			)}
			<div className="card-header bg-white p-3">
				<div className="row">
					<div className="col-md-12">
						<div className="d-flex justify-content-between align-items-center">
							<h5 className="card-title mb-0 text-primary">{name || 'Notas'}</h5>
							<div className="d-flex align-items-center gap-2">
								{!isEditing ? (
									<button
										className="btn-icon"
										onClick={toggleEdit}
										title="Editar notas"
										disabled={isLoading}
									>
										<i className="bi bi-pencil"></i>
									</button>
								) : (
									<button
										className="btn-icon"
										onClick={handleSave}
										title="Guardar notas"
										disabled={isSaving}
									>
										{isSaving ? (
											<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
										) : (
											<i className="bi bi-floppy"></i>
										)}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="card-body p-0">
				{isLoading ? (
					<div className="text-center py-4">
						<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
						<span className="ms-2">Cargando...</span>
					</div>
				) : (
					<>
						{isEditing ? (
							<TextEditor
								text={notesContent}
								setText={setNotesContent}
								border={false}
								toolbar="bold italic | forecolor backcolor | fontsize | lineheight | alignleft aligncenter alignright alignjustify | bullist numlist link | table tabledelete"
							/>
						) : (
							<div
								className={`notes-content ${!notesContent ? 'empty' : ''}`}
								dangerouslySetInnerHTML={{ __html: notesContent || 'No hay notas. Haz clic en el lápiz para añadir.' }}
							/>
						)}
					</>
				)}
			</div>
			{files.length > 0 && (
				<div className="files-section">
					{files.map(file => (
						<div className="file-item" key={file.id}>
							<i className={getFileIcon(file)}></i>
							<a onClick={(e) => handleFileClick(e, file)} title={file.name}>{file.name}</a>
						</div>
					))}
				</div>
			)}
			<div className="card-footer bg-white d-flex justify-content-between align-items-center py-2">
				<small className="text-muted">{files.length > 0 ? `${files.length} archivo${files.length !== 1 ? 's' : ''}` : 'Sin archivos'}</small>
				<div className="d-flex gap-2">
				<button
					className="btn-icon"
					onClick={() => fileInputRef.current?.click()}
					title="Adjuntar archivo"
					disabled={isUploading || !folderId}
				>
					{isUploading ? (
						<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
					) : (
						<i className="bi bi-paperclip"></i>
					)}
				</button>
				<button
					className="btn-icon"
					onClick={openDocumentsFolder}
					title="Abrir gestor documental"
					disabled={!folderId}
				>
					<i className="bi bi-folder2-open"></i>
				</button>
				</div>
			</div>
			<input
				type="file"
				multiple
				hidden
				ref={fileInputRef}
				onChange={handleFileInputChange}
			/>
		</StyledCard>
	);
}
